import type { Archetype } from './types';

/**
 * Fixed Paradox profiles. RunConfig stores only the level so saves, replays and
 * co-op packets stay compact; every gameplay and reward override is resolved
 * from this table.
 */
export interface ParadoxDef {
  level: number;
  name: string;
  description: string;
  enemyHp: number;
  enemyDamage: number;
  enemySpeed: number;
  density: number;
  eliteChanceAdd: number;
  bossHp: number;
  hazardDamage: number;
  shardReward: number;
  score: number;
  spawnWeights: Partial<Record<Archetype, number>>;
}

export const PARADOX_DEFS: readonly ParadoxDef[] = [
  {
    level: 0,
    name: 'Standard',
    description: 'The stable timeline. No Paradox mutators.',
    enemyHp: 1,
    enemyDamage: 1,
    enemySpeed: 1,
    density: 1,
    eliteChanceAdd: 0,
    bossHp: 1,
    hazardDamage: 1,
    shardReward: 1,
    score: 1,
    spawnWeights: {},
  },
  {
    level: 1,
    name: 'Overrun',
    description: 'Larger swarms, more elites, and tougher enemies. +25% Chronoshards.',
    enemyHp: 1.15,
    enemyDamage: 1.1,
    enemySpeed: 1.02,
    density: 1.12,
    eliteChanceAdd: 0.01,
    bossHp: 1,
    hazardDamage: 1,
    shardReward: 1.25,
    score: 1.3,
    spawnWeights: { swarmer: 1.25, splitter: 1.1 },
  },
  {
    level: 2,
    name: 'Fractured',
    description: 'Faster specialist enemies, deadlier hazards, and reinforced bosses. +50% Chronoshards.',
    enemyHp: 1.3,
    enemyDamage: 1.22,
    enemySpeed: 1.05,
    density: 1.2,
    eliteChanceAdd: 0.02,
    bossHp: 1.1,
    hazardDamage: 1.12,
    shardReward: 1.5,
    score: 1.6,
    spawnWeights: { swarmer: 1.15, shooter: 1.2, charger: 1.25, splitter: 1.1, tank: 1.1 },
  },
  {
    level: 3,
    name: 'Collapse',
    description: 'Maximum density, lethal specialists, empowered hazards, and apex bosses. +75% Chronoshards.',
    enemyHp: 1.45,
    enemyDamage: 1.35,
    enemySpeed: 1.08,
    density: 1.28,
    eliteChanceAdd: 0.03,
    bossHp: 1.2,
    hazardDamage: 1.25,
    shardReward: 1.75,
    score: 1.9,
    spawnWeights: { swarmer: 1.1, shooter: 1.25, charger: 1.35, tank: 1.25, splitter: 1.2, elite: 1.2 },
  },
];

export function paradoxDef(level: number): ParadoxDef {
  const index = Math.max(0, Math.min(PARADOX_DEFS.length - 1, Math.floor(level)));
  return PARADOX_DEFS[index];
}

export function paradoxLabel(level: number): string {
  const def = paradoxDef(level);
  return def.level === 0 ? def.name : `Paradox ${def.level} · ${def.name}`;
}
